import { shouldRevalidateProductPage } from '~/modules/root/util/should-revalidate';
import { data as routerData } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import type { SerializeFrom } from '~/types/serialize-from';
import { CornerRightDown } from 'lucide-react';
import type { ElementType } from 'react';
import { Fragment, useMemo, useRef, useState } from 'react';
import type { MetaFunction } from 'react-router';
import type { ShouldRevalidateFunction } from 'react-router';
import {
    useFetcher,
    useLoaderData,
    useLocation,
    useSearchParams,
} from 'react-router';
import invariant from 'tiny-invariant';
import { getPreloadImageLink } from '@modules/images';
import {
    createMetaTitle,
    createMetaDescription,
    createOGTags,
    fallbackOGSocialImage,
    getCanonicalHreflang,
    mergeMeta,
} from '@modules/seo';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { createProductSchema } from '@modules/seo/schema/product-schema';
import { createVideoSchema } from '@modules/seo/schema/video-schema';
import { getMetaBaseUrl } from '@modules/seo/util/create-meta-base-url';
import { createLDSchema } from '@modules/seo/util/create-ld-schema';
import { collectProductVideos } from '@modules/product/util/collect-product-videos';
import createProductOGTags from '@modules/product/util/create-product-og-tags';
import type { loader as rootLoader } from '~/root';
import Variants from '@modules/product/component/variants/Variants';
import ProductDetails from '@modules/product/component/product-details/ProductDetails';
import Description from '@modules/product/component/product-description/Description';
import Specifications from '@modules/product/component/product-details/Specifications';
import { VariantSelectionProvider } from '@modules/product/context/VariantSelectionContext';
import PageHeader from '@modules/product/component/page-header/PageHeader';
import ProductHeaderCTA from '@modules/product/component/page-header/ProductHeaderCTA';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import AboutSeller from '@modules/product/component/about-seller/AboutSeller';
import type {
    GetFeaturedProjectsQuery,
    GetFeaturedProjectsQueryVariables,
    GetProductMainQuery,
    GetProductMainQueryVariables,
    GetProductShippingQuery,
    GetProductShippingQueryVariables,
    GetProductVariantsProfessionalPricingQuery,
    GetProductVariantsProfessionalPricingQueryVariables,
    GetRegionsQuery,
    GetRegionsQueryVariables,
    GetShoppingCartSummaryQuery,
    GetShoppingCartSummaryQueryVariables,
} from 'generated/graphql';
import { PermissionEnum } from 'generated/graphql';
import { getProductSpecifiers } from '~/modules/product/api/get-product-specifiers.server';
import { GetFeaturedProjectsDocument } from 'generated/documents/GetFeaturedProjectsDocument';
import { GetProductMainDocument } from 'generated/documents/GetProductMainDocument';
import { GetProductShippingDocument } from 'generated/documents/GetProductShippingDocument';
import { GetProductVariantsProfessionalPricingDocument } from 'generated/documents/GetProductVariantsProfessionalPricingDocument';
import { GetRegionsDocument } from 'generated/documents/GetRegionsDocument';
import { GetShoppingCartSummaryDocument } from 'generated/documents/GetShoppingCartSummaryDocument';
import { useAppDisplayModeContext, Breadcrumb } from '@archipro-design/aria';
import { VariantOptionProvider } from '@modules/product/context/VariantOptionContext';
import ProductGalleryMobile from '@modules/product/component/photo-gallery/ProductGallery.mobile';
import ProductGalleryDesktop from '~/modules/product/component/photo-gallery/ProductGallery.desktop';
import { getBMPagePath } from '@modules/root/util/get-bm-page-path';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import {
    assertResponse,
    response404,
} from '~/modules/root/graphql/assertResponse.server';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { uniqueOriginals } from '@modules/business-manager/component/detail-page-buttons/download-images';
import { extractBranchFromNetwork } from '@modules/enquiry/util/extract-branch-from-network.server';
import {
    getPermissions,
    useEditPermissions,
    usePermissions,
} from '@modules/user';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import type { APHandle } from '@modules/root';
import { hasFeature } from '@modules/root';
import type { ProductCTAResult } from '@modules/professional/util/professional-cta-helper';
import {
    getProductCTA,
    getProductPageCTA,
} from '@modules/professional/util/professional-cta-helper';
import { createWarningMessages } from '@modules/root/hook/use-warning-messages';
import { processProduct } from '~/modules/product/util/process-product.server';
import { processCart } from '~/modules/product/util/process-cart.server';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import type { loader as appLoader } from '~/routes/_app';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { getSession } from '~/modules/root/util/session.server';
import ProductLocationsDrawer from '~/modules/product/component/product-details/ProductLocationsDrawer';
import { getProfessionalLocations } from '~/modules/product/util/get-professional-locations';
import { DetailsPageRelatedTileGrid } from '~/ui-rocco/pages/details-page/DetailsPageRelatedTileGrid';
import { ProductPageRevamp } from '~/ui-rocco/pages/details-page/revamp/ProductPageRevamp';
import {
    getProductPageCtas,
    type LoadedProductPageCtas,
} from '~/modules/enquiry/api/get-page-ctas.server';
import type { LoadedPageCtas } from '~/modules/enquiry/domain/page-ctas';
import { withArchiproSpecSheet } from '~/modules/product/tear-sheet/archipro-spec-sheet';
import { PRODUCT_GALLERY_SLIDE_SIZES } from '~/ui-rocco/pages/details-page/revamp/sections/gallery-geometry';
import getGallerySlides from '@modules/product/util/get-gallery-slides';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import {
    fetchSimilarProductsPage,
    SIMILAR_PRODUCTS_PAGE_SIZE,
} from '@modules/product/util/fetch-similar-products.server';
import { mapProjectToTileViewModel } from '~/modules-rocco/projects/data-mappers/tile-project';
import { FEATURED_PROJECTS_PAGE_SIZE } from '~/modules/product/util/featured-projects-config';
import { Link } from 'react-router';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';
import { getProductRangeUrl } from '~/modules/product/util/get-product-range-url';
import { getProductCategoryUrl } from '~/modules/product/util/get-product-category-url';
import { variantHasPrice } from '@modules/product/util/variant-has-price';
import { useSetAtom } from 'jotai';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';
import { useEffect } from 'react';
import type { AssociatedPagesType } from '~/utils/country-switch';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { useGateSubject } from '~/modules-rocco/user/auth-gate/gate-subject';
import { useSetInLibrary } from '~/modules/directory/hook/use-set-in-library';
import { useAppData } from '~/modules/root/hook/use-app-data';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { toast } from 'sonner';
import {
    DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
    isDuplicateVisibleRelatedProductGrid,
    shouldShowRelatedProductGridCta,
} from '~/modules/product/util/related-product-grid';
import {
    RELATED_CATEGORY_PRODUCTS_API_ROUTE,
    type RelatedCategoryProductsApiResponse,
} from '~/routes/remix-api.app.product.related-category-products';

/**
 * The revamped product page's ground.
 *
 * The same colour is declared three times over, in three channels that cannot
 * read each other: `ground-grey` in rocco's entrypoint for the bands, the
 * canvas rule in `packages/web/app/tailwind.css` for the strip behind the
 * status bar, and here for Safari's own chrome. A Tailwind class has to stay a
 * literal to be compiled at all, so this constant serves the one channel that
 * takes a value.
 */
const PRODUCT_PAGE_GROUND = '#f5f5f7';

export const meta = mergeMeta<typeof loader>(
    ({ data }) => {
        // Tint Safari's chrome with the page, as the phone's top nav already
        // is. The root emits `theme-color: #FFFFFF` for every page, which put a
        // white bar across the bottom of a grey one; this replaces it by name.
        // Legacy stays white, along with the rest of the legacy page.
        const revamped = (data as { detailsRevamp?: boolean } | undefined)
            ?.detailsRevamp;

        return revamped
            ? [{ name: 'theme-color', content: PRODUCT_PAGE_GROUND }]
            : [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const fallbackProductTitle =
            data?.product?.Professional?.Title &&
            data?.product?.Title &&
            `${data.product.Professional.Title} ${data.product.Title}`;

        const title = createMetaTitle(props, [
            data?.localisation?.DirectoryHeader?.Title,
            fallbackProductTitle,
        ]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            data?.product?.Content,
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.product?.LibraryImages?.[0]?.LibraryImageFile
                ?.DetailPageSquare?.Desktop,
        );

        const productSchema = createProductSchema(props);

        // Gallery videos get their own VideoObject so the product is eligible for video rich results.
        const videoSchemas = collectProductVideos(
            data?.product?.LibraryVideos,
            getMetaBaseUrl(props),
        ).map((video) =>
            createVideoSchema({
                name: video.title ?? data?.product?.Title ?? '',
                publishDate: video.dateCreated,
                professionalName: data?.product?.Professional?.Title,
                url: video.url,
                thumbnailUrl: video.thumbnailUrl,
                description: video.title || description,
            }),
        );

        const ldSchema = createLDSchema([productSchema ?? [], ...videoSchemas]);

        const preloadImages = prefetchProductImages(props);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.product?.FacebookPreviewImage ?? fallbackSocialImage,
            }),
            ...createProductOGTags(
                data?.product,
                data?.shoppingCartSummary?.Currency,
            ),
            ...getCanonicalHreflang(props, {
                professionalCountry: data?.product?.Professional?.Country,
            }),
            ...ldSchema,
            ...(preloadImages ?? []),
            ...noIndexCrossCountry(
                data?.product?.Professional?.Country,
                rootData?.localisation?.country,
                data?.localisation,
            ),
        ];
    },
);

export const links = () => [
    {
        rel: 'stylesheet',
        href: '/assets/website/styles/swiper@9.1.0/modules/free-mode.min.css',
    },
];

const prefetchProductImages: MetaFunction<
    typeof loader,
    { root: typeof rootLoader; 'routes/_app': typeof appLoader }
> = (props) => {
    const { data } = props;
    if (!data?.product) {
        return [];
    }

    // BC-4521: the revamp gallery's LCP is its first slide (which is not
    // always the DefaultImage) rendered at the editorial slide width — preload
    // that exact variant (same src + sizes as the gallery `Image`) so the
    // browser reuses it and the slide paints without the grey flash.
    if (data.detailsRevamp) {
        const firstSlide = getGallerySlides({
            LibraryImages: data.product.LibraryImages,
            LibraryVideos: data.product.LibraryVideos,
            DiagramImages: data.product.DiagramImages,
        })[0];
        if (!firstSlide || isLibraryVideo(firstSlide)) return [];
        const natural = firstSlide.LibraryImageFile?.DetailPageNatural;
        if (!natural?.Desktop) return [];
        const imageUrl =
            natural.Mobile && data.deviceType === 'mobile'
                ? natural.Mobile
                : natural.Desktop;
        return [
            ...getPreloadImageLink(
                imageUrl,
                undefined,
                PRODUCT_GALLERY_SLIDE_SIZES,
            ),
        ];
    }

    const defaultImage =
        data.product.DefaultImage?.LibraryImageFile?.DetailPageSquare;
    const { Desktop, Mobile } = defaultImage ?? {};

    if (!data?.product) return [];

    if (!Desktop) {
        return [];
    }

    if (Mobile && data.deviceType === 'mobile') {
        return [
            ...getPreloadImageLink(
                Mobile,
                undefined,
                getImagesSizes({ desktop: 780, mobile: 375 }),
            ),
        ];
    }

    return [...getPreloadImageLink(Desktop, undefined, getImagesSizes(780))];
};

export const handle: APHandle = {
    fadeTopNavAfterPosition: 200,
    hideContactArchiPro: true,
    // BC-4505: the revamp uses the unified header's logomark so the brand mark
    // is consistent with the rest of the page (rather than a content logomark).
    // Resolved against loader data so the legacy page keeps the compact header
    // while `revamp_2026` is off.
    header: (loaderData) =>
        (loaderData as { detailsRevamp?: boolean } | undefined)?.detailsRevamp
            ? {
                  showLogomark: true,
                  // The phone page is one grey ground top to bottom; a white
                  // bar over it read as a band. Desktop keeps the white bar.
                  ground: 'bg-[#f5f5f7] md:bg-white',
              }
            : undefined,
};

/**
 * The purchase drawer shows the cart summary this loader returns, so cart
 * updates revalidate it. Add-to-cart does not: the drawer opens from that
 * action's own response.
 */
export const shouldRevalidate: ShouldRevalidateFunction =
    shouldRevalidateProductPage;

/** The product and its professional, once the pricing query names them. */
const pricingProductIds = (result: {
    data?: GetProductVariantsProfessionalPricingQuery | null;
}): { productId?: number; professionalId?: number } => {
    const node = result.data?.Directory?.Product;
    return node?.__typename === 'Product'
        ? { productId: node.ID, professionalId: node.Professional?.ID }
        : {};
};

/**
 * The wording that matches what the shopper sees. A function rather than an
 * inline ternary: narrowing on the price flag inside the loader walks back
 * through the variant types, which derive from this loader's own return type.
 */
const pickProductPageCtas = (
    ctas: LoadedProductPageCtas | null,
    hasVisiblePrice: boolean,
): LoadedPageCtas | null =>
    (hasVisiblePrice ? ctas?.priced : ctas?.unpriced) ?? null;

export const loader = async (args: LoaderFunctionArgs) => {
    const { request, params, context } = args;
    invariant(params.urlSegment, 'A product URL segment is required');

    const { user, featureFlags } = context;
    const searchParams = new URL(request.url).searchParams;
    const revampActive =
        hasFeature(featureFlags, 'revamp_2026') ||
        (process.env.NODE_ENV === 'development' &&
            (searchParams.get('revamp') === '1' ||
                searchParams.get('detailsRevamp') === '1'));
    const productPromise = context.client.query<
        GetProductMainQuery,
        GetProductMainQueryVariables
    >({
        query: GetProductMainDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });

    const productVariantsProfessionalPricingPromise = context.client.query<
        GetProductVariantsProfessionalPricingQuery,
        GetProductVariantsProfessionalPricingQueryVariables
    >({
        query: GetProductVariantsProfessionalPricingDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });

    const productShippingPromise = context.client.query<
        GetProductShippingQuery,
        GetProductShippingQueryVariables
    >({
        query: GetProductShippingDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });
    const cartSummaryPromise = context.client.query<
        GetShoppingCartSummaryQuery,
        GetShoppingCartSummaryQueryVariables
    >({
        query: GetShoppingCartSummaryDocument,
    });

    const regionPromise = context.client.query<
        GetRegionsQuery,
        GetRegionsQueryVariables
    >({
        query: GetRegionsDocument,
        variables: {
            country: context.localisation.country,
            public: true,
        },
    });

    const getFeaturedProjectsLimit = (): number => {
        // The revamp's tagged-projects grid SSRs the first page and appends the
        // rest via "Load More"; the legacy page keeps its 5-then-view-more.
        if (revampActive) return FEATURED_PROJECTS_PAGE_SIZE;
        return searchParams.get('featuredProjectsShowAll') === '1' ? 0 : 5;
    };

    const featuredProjectsPromise = context.client.query<
        GetFeaturedProjectsQuery,
        GetFeaturedProjectsQueryVariables
    >({
        query: GetFeaturedProjectsDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
            limit: getFeaturedProjectsLimit(),
            offset: 0,
        },
    });

    const localisationPromise = getPageLocalisation(args);

    // Revamp only: SSR the first page of the "Similar Products" grid (same
    // category, directory ElasticSearch) so the accordion row / mobile section
    // can be gated on real data and the grid renders without a client flash.
    // "Load More" pages come from /actions/load-more-products. Chained off the
    // product query (it needs the category link), so it overlaps the other
    // parallel queries instead of running after all of them.
    const similarProductsPromise = productPromise.then((result) => {
        const node = result.data?.Directory?.Product;
        const rawProduct = node?.__typename === 'Product' ? node : null;
        const categoryLink = rawProduct?.Categories?.[0]?.Link;
        if (!revampActive || !rawProduct || !categoryLink) return null;
        return fetchSimilarProductsPage({
            context,
            categoryLink,
            currentProductId: rawProduct.ID,
            offset: 0,
            limit:
                context.appDisplayMode === 'mobile'
                    ? SIMILAR_PRODUCTS_PAGE_SIZE.mobile
                    : SIMILAR_PRODUCTS_PAGE_SIZE.desktop,
        });
    });

    // Revamp only: who specifies the product, for the meta row's "Specified
    // by" list. Chained off the product query (it needs the supplier) and
    // allowed to fail softly: without it the list just doesn't show.
    const specifiersPromise = productPromise
        .then((result) => {
            const node = result.data?.Directory?.Product;
            const rawProduct = node?.__typename === 'Product' ? node : null;
            const supplier = rawProduct?.Professional;
            if (!revampActive || !supplier?.URLSegment) return null;
            return getProductSpecifiers({
                context,
                link: `/product/${params.urlSegment}`,
                supplier: supplier.URLSegment,
                supplierId: supplier.ID,
            });
        })
        .catch(() => null);

    // Both enquire wordings, so the lookup overlaps the product queries rather
    // than waiting on the visible-price check it depends on. Never rejects.
    const pageCtasPromise = productVariantsProfessionalPricingPromise.then(
        (result) => {
            const { productId, professionalId } = pricingProductIds(result);
            return getProductPageCtas(context, professionalId, productId);
        },
        () => null,
    );

    const [
        productResult,
        productVariantsProfessionalPricingResult,
        productShippingResult,
        cartSummaryResult,
        regionResult,
        featuredProjectsResult,
        localisation,
        similarProducts,
        specifiers,
        productPageCtas,
    ] = await Promise.all([
        productPromise,
        productVariantsProfessionalPricingPromise,
        productShippingPromise,
        cartSummaryPromise,
        regionPromise,
        featuredProjectsPromise,
        localisationPromise,
        similarProductsPromise,
        specifiersPromise,
        pageCtasPromise,
    ]);

    unsafeVariant_DO_NOT_USE(productResult);
    unsafeVariant_DO_NOT_USE(productVariantsProfessionalPricingResult);
    unsafeVariant_DO_NOT_USE(productShippingResult);
    unsafeVariant_DO_NOT_USE(cartSummaryResult);
    unsafeVariant_DO_NOT_USE(regionResult);
    unsafeVariant_DO_NOT_USE(featuredProjectsResult);

    const product = await assertResponse(
        args.context,
        productResult.data.Directory.Product,
        productResult.error,
    );

    const productShipping = await assertResponse(
        args.context,
        productShippingResult.data.Directory.Product,
        productShippingResult.error,
    );

    const productVariantsProfessionalPricing = await assertResponse(
        args.context,
        productVariantsProfessionalPricingResult.data.Directory.Product,
        productVariantsProfessionalPricingResult.error,
    );

    const cartSummary = await assertResponse(
        args.context,
        cartSummaryResult.data.ShoppingCart,
        cartSummaryResult.error,
    );
    invariant(cartSummary, 'Failed to load cart');

    const regions = regionResult.data.Regions;
    invariant(regions, 'Failed to load regions');

    const featuredProjects = await assertResponse(
        args.context,
        featuredProjectsResult.data.Directory.Product,
        featuredProjectsResult.error,
    );

    const primaryCategory = product.Categories?.[0];

    if (!productVariantsProfessionalPricing.Professional) {
        throw response404();
    }

    const networks = productVariantsProfessionalPricing.Professional.Networks;
    const branchOptions = extractBranchFromNetwork(networks);

    const processedProduct = processProduct(
        product,
        productVariantsProfessionalPricing,
        productShipping,
        user,
        args.context.appDisplayMode,
    );

    const processedCart = processCart(
        cartSummary,
        processedProduct.Professional,
    );

    const processedFeaturedProjects = {
        FeaturedProjects: {
            pageInfo: {
                totalCount:
                    featuredProjects.FeaturedProjects.pageInfo.totalCount,
                hasNextPage:
                    featuredProjects.FeaturedProjects.pageInfo.hasNextPage,
            },
            edges: featuredProjects.FeaturedProjects.edges.map((edge) => {
                return {
                    node: mapProjectToTileViewModel(edge.node, {
                        appDisplayMode: context.appDisplayMode,
                        imageToDisplay: 'featured', // use the correction Thumbnail via PositionedImage
                    }),
                };
            }),
        },
    };

    // Auto-optimise swaps "Request Price / Brochure" for "Enquire Now" once the
    // shopper can already see a price, so this has to track what the page puts
    // on screen. Both pages now gate a price the same way — an amount, pricing
    // not switched off for the professional, and a variant this user may see
    // the price of — so the arms differ only in which variants they consider.
    //
    // The revamp counts the master variant, because a product with no variants
    // prices off it; checking `Variants` alone left those products asking for a
    // price printed right beside the button. Legacy keeps its original test
    // verbatim: it is live, and widening it there would move labels in
    // production. Retire the branch with the legacy page.
    const priceCandidates = processedProduct.Variants.length
        ? processedProduct.Variants
        : [processedProduct.MasterVariant].filter((v) => v !== null);
    const productHasVisiblePrice = revampActive
        ? !processedProduct.Professional.PricingDisabled &&
          priceCandidates.some(
              (variant) =>
                  variant.CanViewPrice &&
                  variantHasPrice(
                      variant,
                      processedProduct.Pricing.PricingType,
                  ),
          )
        : processedProduct.Variants.some((variant) => variant.CanViewPrice);

    // Product pages show the Product Call-to-Action setting. Primary CTA is
    // the brand/profile button and is a separate dropdown. This object is
    // what every product-page enquire button reads — legacy and revamp_2026,
    // desktop and mobile (header, details, photo viewer, purchase cluster,
    // island, enquiry prompt).
    const primaryCTA = getProductPageCTA({
        productCTA: processedProduct.Professional.ProductCTA,
        isManufacturer: processedProduct.Professional.IsManufacturer,
        productHasVisiblePrice,
    });

    const pageCtas = pickProductPageCtas(
        productPageCtas,
        productHasVisiblePrice,
    );

    const productCTA: ProductCTAResult = getProductCTA({
        productCTA: processedProduct.Professional.ProductCTA,
        pageType: 'product',
        isManufacturer: processedProduct.Professional.IsManufacturer,
    });

    // Create warning message for the WarningBanner
    const warningMessages = createWarningMessages(
        processedProduct.Professional.Country,
        context.localisation.country,
        !processedProduct.IsLive,
        'product',
        processedProduct?.Professional.Disable,
        {
            showPageNotLiveWarning: getPermissions(
                user,
                PermissionEnum.BmSupportView,
            ),
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        },
    );

    const { locations: showrooms, linkedProfiles } = getProfessionalLocations(
        productVariantsProfessionalPricing.Professional,
        ['Showroom'],
    );

    const whereToBuyEnabled =
        hasFeature(featureFlags, 'show_where_to_buy_products') &&
        showrooms.length > 0;

    // Revamp only: prepend the ArchiPro-generated spec sheet to the Spec Sheets
    // group. Kept out of the legacy page so its "Product Files" count and modal
    // are unchanged.
    if (revampActive) {
        processedProduct.LibraryDownloads = withArchiproSpecSheet(
            processedProduct.LibraryDownloads,
            product.Link,
            product.TearSheetSizeBytes,
        );
    }

    // The revamp gallery is now a contained, fixed-height card (not full-bleed
    // to the top), so the header stays solid/white on every breakpoint rather
    // than starting transparent over the imagery.
    const pageTheme = 'default' as const;

    const session = await getSession(request);

    return routerData(
        {
            deviceType: context.device.type,
            product: processedProduct,
            similarProducts,
            specifiers,
            shoppingCartSummary: processedCart,
            regions: regions,
            featuredProjects: processedFeaturedProjects,
            branchOptions,
            localisation,
            primaryCTA,
            productCTA,
            pageCtas,
            warningMessages,
            whereToBuyEnabled,
            showrooms,
            linkedProfiles,
            primaryCategory,
            // Umbrella flag gating the revamp page and the Magic Island.
            // `?detailsRevamp=1` overrides in dev — applied client-side in
            // ProductDetailsMagicIsland.
            detailsRevamp: hasFeature(featureFlags, 'revamp_2026'),
            pageTheme,
        },
        {
            headers: await session.getHeaders(),
        },
    );
};

export type ProductPageLoaderData = SerializeFrom<typeof loader>;

const ProductPageLegacy = () => {
    const {
        product,
        featuredProjects: { FeaturedProjects },
        primaryCTA,
        branchOptions,
        whereToBuyEnabled,
        primaryCategory,
    } = useLoaderData<typeof loader>();

    const { mobile } = useAppDisplayModeContext();
    const appDisplayMode = mobile ? 'mobile' : 'desktop';
    const { setCategory } = useCurrentCategory();

    const { canEditProducts } = useEditPermissions(product.Professional.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmMarketingView,
        product.Professional.ID,
    );
    const productEditPath = getBMPagePath(
        product.Professional.URLSegment,
        'products',
        product.URLSegment,
    );
    useSetEditMode(productEditPath, canEditProducts);

    const { user } = useAppData();
    const isAdminUser =
        user.__typename === 'Me' &&
        (user.IsStaffAdmin === true ||
            ('IsHighAdmin' in user && user.IsHighAdmin === true));
    const [optimisticInLibrary, setOptimisticInLibrary] = useState(
        !!product.InLibrary,
    );
    useEffect(() => {
        setOptimisticInLibrary(!!product.InLibrary);
    }, [product.InLibrary]);
    const { setInLibrary } = useSetInLibrary(
        (error) => {
            setOptimisticInLibrary(!!product.InLibrary);
            toast.error(error.Message || 'Something went wrong');
        },
        (_, __, inLibrary) =>
            toast.success(
                inLibrary ? 'Added to directory.' : 'Removed from directory.',
            ),
    );

    //sections to hide the sticky action.
    const primaryCTASectionRef = useRef<HTMLDivElement>(null);
    const enquireSectionRef = useRef<HTMLDivElement>(null);
    const extrasSectionRef = useRef<HTMLDivElement>(null);
    const productRangeSectionRef = useRef<HTMLDivElement>(null);
    const scrollAnimationFrameRef = useRef<number | null>(null);
    const scrollInterruptCleanupRef = useRef<(() => void) | null>(null);
    const [whereToBuyOpen, setWhereToBuyOpen] = useState(false);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const showAllFeaturedProjects =
        searchParams.get('featuredProjectsShowAll') === '1';
    const setCountryData = useSetAtom(setCloneCountryStateAtom);
    const categoryProductsFetcher =
        useFetcher<RelatedCategoryProductsApiResponse>({
            key: `product-related-category-${product.ID}-${appDisplayMode}`,
        });

    const scrollToProductRangeSection = () => {
        const targetSection = productRangeSectionRef.current;

        if (!targetSection) {
            return;
        }

        if (scrollAnimationFrameRef.current !== null) {
            window.cancelAnimationFrame(scrollAnimationFrameRef.current);
            scrollAnimationFrameRef.current = null;
        }

        scrollInterruptCleanupRef.current?.();
        scrollInterruptCleanupRef.current = null;

        if (
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            targetSection.scrollIntoView({
                block: 'start',
            });

            return;
        }

        const scrollStart = window.scrollY;
        const scrollOffsetPx = 30;
        const maxScrollY =
            document.documentElement.scrollHeight - window.innerHeight;
        const targetY = Math.min(
            Math.max(
                0,
                targetSection.getBoundingClientRect().top +
                    window.scrollY -
                    scrollOffsetPx,
            ),
            maxScrollY,
        );
        const scrollDistance = targetY - scrollStart;
        const absoluteScrollDistance = Math.abs(scrollDistance);

        if (absoluteScrollDistance < 12) {
            window.scrollTo({
                top: targetY,
            });

            return;
        }

        const animationDurationMs = Math.min(
            Math.max(640, 580 + absoluteScrollDistance * 0.32),
            1325,
        );
        const animationStartTime = performance.now();

        const easeInOutQuart = (progress: number) => {
            if (progress < 0.5) {
                return 8 * progress * progress * progress * progress;
            }

            return 1 - Math.pow(-2 * progress + 2, 4) / 2;
        };

        const stopScrollAnimation = () => {
            if (scrollAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(scrollAnimationFrameRef.current);
                scrollAnimationFrameRef.current = null;
            }

            scrollInterruptCleanupRef.current?.();
            scrollInterruptCleanupRef.current = null;
        };

        const interruptScrollAnimation = () => {
            stopScrollAnimation();
        };

        const interruptEvents: Array<keyof WindowEventMap> = [
            'wheel',
            'touchstart',
            'mousedown',
            'keydown',
        ];

        interruptEvents.forEach((eventName) => {
            window.addEventListener(eventName, interruptScrollAnimation, {
                passive: true,
            });
        });

        scrollInterruptCleanupRef.current = () => {
            interruptEvents.forEach((eventName) => {
                window.removeEventListener(eventName, interruptScrollAnimation);
            });
        };

        const animateScroll = (currentTime: number) => {
            const elapsed = currentTime - animationStartTime;
            const progress = Math.min(elapsed / animationDurationMs, 1);
            const easedProgress = easeInOutQuart(progress);
            const nextScrollTop = scrollStart + scrollDistance * easedProgress;

            window.scrollTo({
                top: nextScrollTop,
            });

            if (progress < 1) {
                scrollAnimationFrameRef.current =
                    window.requestAnimationFrame(animateScroll);

                return;
            }

            window.scrollTo({
                top: targetY,
            });

            stopScrollAnimation();
        };

        scrollAnimationFrameRef.current =
            window.requestAnimationFrame(animateScroll);
    };

    // Website referral tracking for photo viewer CTA
    const {
        referralTrackingURL: photoViewerWebsiteUrl,
        referralTrackingParams,
    } = useWebsiteReferralTracking(product.Professional.Website, {
        libraryItemID: product.ID,
        professionalID: product.Professional.ID,
        professionalTitle: product.Professional.Title,
        source: 'ProductPhotoViewer',
    });

    const enquiryModalConfig = {
        professional: {
            ID: product.Professional.ID,
            Title: product.Professional.Title,
            branchOptions,
            CompanyLogo: product.Professional.CompanyLogo ?? '',
            LogoBackgroundColor:
                product.Professional.LogoBackgroundColor ?? 'white',
            CustomEnquiryMessage:
                product.Professional.CustomEnquiryMessage ?? undefined,
            EnquiryMessageOverrideEnabled:
                product.Professional.EnquiryMessageOverrideEnabled ?? false,
            EnquiryMessagePlaceholder:
                product.Professional.EnquiryMessagePlaceholder ?? undefined,
        },
        itemType: 'product' as const,
        siteTreeID: product.ID,
        ctaLabel: primaryCTA.primaryCTALabelTitleCase,
        includeBranch: branchOptions.length > 0,
        itemTitle: product.Title,
        itemImage:
            product.LibraryImages?.[0]?.LibraryImageFile?.PhotoTile ??
            undefined,
    };

    // Above-the-fold header CTA respects the professional's enquiry audience setting
    const { showEnquiryCTA: showHeaderEnquiryCTA } = useEnquiryVisibility(
        product.Professional,
        { productId: product.ID },
    );

    // Enquiry modal for photo viewer CTA
    const { openEnquiryModal: openPhotoViewerEnquiry } = useEnquiryModal({
        ...enquiryModalConfig,
        source: 'ProductPhotoViewer',
    });

    // Enquiry modal for mobile "Request Price" button
    const { openEnquiryModal: openMobileEnquiry } = useEnquiryModal({
        ...enquiryModalConfig,
        source: 'ProductMobileCTA',
    });

    useEffect(() => {
        if (!primaryCategory?.ID || !product.Professional.Link) {
            return;
        }

        const params = new URLSearchParams({
            source: product.Professional.Link,
            categoryID: String(primaryCategory.ID),
            currentProductID: String(product.ID),
            appDisplayMode,
        });

        categoryProductsFetcher.load(
            `${RELATED_CATEGORY_PRODUCTS_API_ROUTE}?${params.toString()}`,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- categoryProductsFetcher identity changes every state update; including it would retrigger after load() and loop forever.
    }, [
        appDisplayMode,
        primaryCategory?.ID,
        product.ID,
        product.Professional.Link,
    ]);

    const categoryProducts = categoryProductsFetcher.data?.categoryProducts ?? {
        products: [],
        pageInfo: { totalCount: 0 },
    };
    const isLoadingCategoryProducts =
        categoryProductsFetcher.state === 'loading' &&
        categoryProducts.products.length === 0;
    const categoryProductsTitle = primaryCategory ? (
        <>
            Other products in{' '}
            <Link
                to={getProductCategoryUrl(
                    product.Professional,
                    primaryCategory,
                )}
                className="text-[#b6b6b6] hover:text-gray-400"
            >
                {primaryCategory.PrettyTitle}
            </Link>{' '}
            by{' '}
            <Link
                to={`${product.Professional.Link}/our-products`}
                className="text-[#b6b6b6] hover:text-gray-400"
            >
                {product.Professional.Title}
            </Link>
        </>
    ) : null;

    const productRangeTiles: TileViewModel<ProductTileViewModel>[] = useMemo(
        () =>
            product.ProductRange.products.map((product) => {
                return {
                    model: product,
                    aspectRatio: 'square',
                    section: 'ProductRange',
                };
            }),
        [product.ProductRange.products],
    );
    const featuredProjectsTiles: TileViewModel<ProjectTileViewModel>[] =
        useMemo(
            () =>
                FeaturedProjects.edges.map(({ node }) => {
                    return {
                        model: node,
                        aspectRatio: 'default',
                        section: 'ProductFeaturedProjects',
                    };
                }),
            [FeaturedProjects.edges],
        );

    const alsoFromProductsTiles: TileViewModel<ProductTileViewModel>[] =
        useMemo(
            () =>
                product.AlsoFromProducts.products.map((product) => {
                    return {
                        model: product,
                        aspectRatio: 'square',
                        section: 'ProductRelated',
                    };
                }),
            [product.AlsoFromProducts.products],
        );
    const installersTiles: TileViewModel<ProfessionalTileViewModel>[] = useMemo(
        () =>
            product.Installers.professionals.map((professional) => {
                return {
                    model: professional,
                    aspectRatio: 'default',
                    section: 'ProductInstallers',
                };
            }),
        [product.Installers.professionals],
    );
    const categoryProductsTiles: TileViewModel<ProductTileViewModel>[] =
        useMemo(
            () =>
                categoryProducts.products.map((categoryProduct) => {
                    return {
                        model: categoryProduct,
                        aspectRatio: 'square',
                        section: 'ProductCategory',
                    };
                }),
            [categoryProducts.products],
        );

    const productRangeAdmin = useTilesAdminData(productRangeTiles);
    const featuredProjectsAdmin = useTilesAdminData(featuredProjectsTiles);
    const alsoFromProductsAdmin = useTilesAdminData(alsoFromProductsTiles);
    const installersAdmin = useTilesAdminData(installersTiles);
    const categoryProductsAdmin = useTilesAdminData(categoryProductsTiles);

    const showProductRange = product.ProductRange.products.length > 0;
    // The our-products listing filters by a single range, so the "View more"
    // CTA only exists when the product belongs to exactly one range.
    const soleRange =
        product.LibraryRanges.length === 1 ? product.LibraryRanges[0] : null;

    // Keep the first product grid and hide later grids that show the same visible products.
    const showCategoryProducts = useMemo(() => {
        if (!primaryCategory || categoryProducts.products.length === 0) {
            return false;
        }

        const earlierProductGrids = showProductRange
            ? [product.ProductRange.products]
            : [];

        return !isDuplicateVisibleRelatedProductGrid(
            categoryProducts.products,
            earlierProductGrids,
            appDisplayMode,
            DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
        );
    }, [
        appDisplayMode,
        categoryProducts.products,
        primaryCategory,
        product.ProductRange.products,
        showProductRange,
    ]);
    const showCategoryProductsPlaceholder =
        !!primaryCategory && isLoadingCategoryProducts;

    const showAlsoFromProducts = useMemo(() => {
        if (
            showProductRange ||
            product.AlsoFromProducts.products.length === 0
        ) {
            return false;
        }

        // Don't show "Also From" while category products are loading to avoid layout shift
        if (isLoadingCategoryProducts) {
            return false;
        }

        const earlierProductGrids = showCategoryProducts
            ? [categoryProducts.products]
            : [];

        return !isDuplicateVisibleRelatedProductGrid(
            product.AlsoFromProducts.products,
            earlierProductGrids,
            appDisplayMode,
            DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
        );
    }, [
        appDisplayMode,
        categoryProducts.products,
        isLoadingCategoryProducts,
        product.AlsoFromProducts.products,
        showCategoryProducts,
        showProductRange,
    ]);

    useEffect(() => {
        setCountryData({
            urlSegment: product?.URLSegment,
            currentCountry: product?.Professional?.Country,
            pageType: 'product',
            associatedPages: product?.AssociatedPages.edges.length
                ? (product?.AssociatedPages as AssociatedPagesType)
                : product?.Professional?.ProfessionalAssociatedPages,
        });
    }, [product, setCountryData]);

    // Set the current category for header navigation
    useEffect(() => {
        setCategory('Products');
        return () => setCategory(null);
    }, [setCategory]);

    useEffect(() => {
        return () => {
            if (scrollAnimationFrameRef.current !== null) {
                window.cancelAnimationFrame(scrollAnimationFrameRef.current);
            }

            scrollInterruptCleanupRef.current?.();
        };
    }, []);

    return (
        <div className="mx-auto max-w-(--ap-page-width) pt-4 pb-15 md:px-12.5 md:pt-0 md:pb-0 qhd:px-0">
            <VariantSelectionProvider product={product}>
                {!mobile && (
                    <PageHeader
                        data={{
                            Breadcrumbs: product.Breadcrumbs,
                            Title: product.Title,
                            Professional: product.Professional,
                        }}
                        isSticky={false}
                        // Mirror the serviceable-area guard on the lower CTA block.
                        cta={
                            product.IsInServiceableArea ? (
                                <ProductHeaderCTA
                                    pageID={product.ID}
                                    website={
                                        product.Website ||
                                        product.Professional.Website
                                    }
                                    primaryCTALabel={
                                        primaryCTA.primaryCTALabelTitleCase
                                    }
                                    enquiryModalConfig={enquiryModalConfig}
                                    showEnquiryCTA={showHeaderEnquiryCTA}
                                    className="mb-3 shrink-0"
                                />
                            ) : undefined
                        }
                    />
                )}
                <div className="space-y-20 md:space-y-60">
                    <VariantOptionProvider product={product}>
                        <div className="grid grid-cols-1 gap-x-11 md:grid-cols-2 md:pt-5">
                            {mobile ? (
                                <ProductGalleryMobile
                                    key={`product-gallery-${product.ID}`}
                                    data={product}
                                    title={product.Title}
                                />
                            ) : (
                                <ProductGalleryDesktop
                                    key={`product-gallery-${product.ID}`}
                                    data={product}
                                    title={product.Title}
                                    loop={true}
                                    cta={{
                                        websiteUrl:
                                            photoViewerWebsiteUrl ?? undefined,
                                        ...(photoViewerWebsiteUrl && {
                                            websiteTrackingParams:
                                                referralTrackingParams,
                                        }),
                                        onEnquire: () =>
                                            openPhotoViewerEnquiry({
                                                title: primaryCTA.primaryCTALabelTitleCase,
                                                ctaLabel:
                                                    primaryCTA.primaryCTALabelTitleCase,
                                            }),
                                        enquiryLabel:
                                            primaryCTA.primaryCTALabelTitleCase,
                                    }}
                                />
                            )}
                            {mobile && (
                                <PageHeader
                                    data={{
                                        Breadcrumbs: product.Breadcrumbs,
                                        Title: product.Title,
                                        Professional: product.Professional,
                                    }}
                                />
                            )}
                            <div>
                                <div className="flex flex-col px-4.5 md:px-0">
                                    <Description
                                        product={product}
                                        showSpecifications={mobile}
                                    />
                                    <div className="-order-1 md:order-0">
                                        <Variants
                                            data={{
                                                product: product,
                                                ecommerceEnabled: true,
                                            }}
                                            whereToBuyEnabled={
                                                whereToBuyEnabled
                                            }
                                            onClickWhereToBuy={() =>
                                                setWhereToBuyOpen(true)
                                            }
                                        />
                                    </div>
                                    {!mobile && (
                                        <Specifications data={product} />
                                    )}
                                    <ProductDetails
                                        data={{
                                            ...product,
                                            primaryCTA,
                                            primaryCTASectionRef,
                                            enquireSectionRef,
                                        }}
                                        branchOptions={branchOptions}
                                        whereToBuyEnabled={whereToBuyEnabled}
                                        onClickWhereToBuy={() =>
                                            setWhereToBuyOpen(true)
                                        }
                                        onEnquire={() =>
                                            openMobileEnquiry({
                                                title: 'Enquire',
                                                ctaLabel:
                                                    primaryCTA.primaryCTALabelTitleCase,
                                            })
                                        }
                                        showDescription={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </VariantOptionProvider>

                    <div
                        ref={extrasSectionRef}
                        className={
                            'flex flex-col gap-y-8 px-4 md:gap-y-[8rem] md:px-0'
                        }
                    >
                        {/* Category Breadcrumbs — transition into related content */}
                        <div className="hidden flex-col gap-3 md:flex">
                            <Breadcrumb
                                logo={null}
                                home={{
                                    link: true,
                                    as: Link,
                                    label: 'Home',
                                    to: '/',
                                    variables: {
                                        breadcrumbItemTextColor: '#b6b6b6',
                                    },
                                }}
                                items={product.Breadcrumbs.map(
                                    (breadcrumb) => ({
                                        label: breadcrumb.label,
                                        href: breadcrumb.href,
                                        as: breadcrumb.as as ElementType,
                                        variables: {
                                            breadcrumbItemTextColor: '#b6b6b6',
                                        },
                                    }),
                                )}
                                data-testid="breadcrumb-menu"
                                variables={{
                                    breadcrumbFontSize: '0.875rem',
                                }}
                            />
                            <button
                                type="button"
                                onClick={scrollToProductRangeSection}
                                className="flex w-fit items-center gap-2 text-6 font-medium tracking-tight text-black transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-black/20 focus-visible:outline-hidden"
                            >
                                <span>More to explore</span>
                                <CornerRightDown
                                    className="relative top-[5px] h-5 w-5 opacity-50"
                                    strokeWidth={2}
                                />
                            </button>
                        </div>

                        {/* Product Range */}
                        <div
                            id="product-range-section"
                            ref={productRangeSectionRef}
                            className="md:scroll-mt-32"
                        >
                            {showProductRange && (
                                <DetailsPageRelatedTileGrid
                                    type="products"
                                    title={
                                        <>
                                            Other products in the{' '}
                                            {product.LibraryRanges.map(
                                                (range, index) => (
                                                    <Fragment key={range.ID}>
                                                        {index > 0 &&
                                                            (index ===
                                                            product
                                                                .LibraryRanges
                                                                .length -
                                                                1
                                                                ? ' and '
                                                                : ', ')}
                                                        <Link
                                                            to={getProductRangeUrl(
                                                                product.Professional,
                                                                range.Title,
                                                            )}
                                                            className="text-[#b6b6b6] hover:text-gray-400"
                                                        >
                                                            {range.Title}
                                                        </Link>
                                                    </Fragment>
                                                ),
                                            )}{' '}
                                            {product.LibraryRanges.length > 1
                                                ? 'ranges'
                                                : 'range'}
                                        </>
                                    }
                                    tiles={productRangeAdmin}
                                    cta={
                                        soleRange &&
                                        shouldShowRelatedProductGridCta(
                                            product.ProductRange.pageInfo
                                                .totalCount,
                                            appDisplayMode,
                                            DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
                                        )
                                            ? {
                                                  type: 'link',
                                                  to: getProductRangeUrl(
                                                      product.Professional,
                                                      soleRange.Title,
                                                  ),
                                                  label: 'View more',
                                              }
                                            : undefined
                                    }
                                    displayCount={
                                        DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT
                                    }
                                    appDisplayMode={appDisplayMode}
                                />
                            )}
                        </div>

                        {/* Products in Same Category - hidden if they duplicate an earlier product grid */}
                        {primaryCategory && showCategoryProducts ? (
                            <DetailsPageRelatedTileGrid
                                type="products"
                                title={categoryProductsTitle}
                                tiles={categoryProductsAdmin}
                                cta={
                                    shouldShowRelatedProductGridCta(
                                        categoryProducts.pageInfo.totalCount,
                                        appDisplayMode,
                                        DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
                                    )
                                        ? {
                                              type: 'link',
                                              to: getProductCategoryUrl(
                                                  product.Professional,
                                                  primaryCategory,
                                              ),
                                              label: 'View more',
                                          }
                                        : undefined
                                }
                                displayCount={
                                    DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT
                                }
                                appDisplayMode={appDisplayMode}
                            />
                        ) : showCategoryProductsPlaceholder ? (
                            <DetailsPageRelatedTileGrid
                                type="products"
                                title={categoryProductsTitle}
                                tiles={[]}
                                showTitleWhenEmpty={true}
                                isLoading={true}
                                displayCount={
                                    DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT
                                }
                                appDisplayMode={appDisplayMode}
                            />
                        ) : null}

                        {/* Projects featuring */}
                        <DetailsPageRelatedTileGrid
                            type="projects"
                            title={`Projects using this product: ${product.Title}`}
                            tiles={featuredProjectsAdmin}
                            cta={
                                FeaturedProjects.pageInfo.totalCount <= 4
                                    ? undefined
                                    : showAllFeaturedProjects
                                      ? {
                                            type: 'link',
                                            to: '?',
                                            label: 'View less',
                                            preventScrollReset: true,
                                        }
                                      : {
                                            type: 'link',
                                            to: '?featuredProjectsShowAll=1',
                                            label: 'View more',
                                            preventScrollReset: true,
                                        }
                            }
                            appDisplayMode={appDisplayMode}
                            displayCount={{
                                desktop: showAllFeaturedProjects
                                    ? FeaturedProjects.pageInfo.totalCount
                                    : 4,
                                mobile: showAllFeaturedProjects
                                    ? FeaturedProjects.pageInfo.totalCount
                                    : 4,
                            }}
                        />

                        {/* Also From Products - hidden if range is shown or they duplicate an earlier product grid */}
                        {showAlsoFromProducts && (
                            <DetailsPageRelatedTileGrid
                                type="products"
                                title={
                                    <>
                                        More from{' '}
                                        <Link
                                            to={`${product.Professional.Link}/our-products`}
                                            className="text-[#b6b6b6] hover:text-gray-400"
                                        >
                                            {product.Professional?.Title}
                                        </Link>
                                    </>
                                }
                                tiles={alsoFromProductsAdmin}
                                cta={
                                    product.AlsoFromProducts.pageInfo
                                        .totalCount > (mobile ? 4 : 10)
                                        ? {
                                              type: 'link',
                                              to: `${product.Professional.Link}/our-products#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                                              label: 'View more',
                                          }
                                        : undefined
                                }
                                appDisplayMode={appDisplayMode}
                                displayCount={
                                    DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT
                                }
                            />
                        )}

                        {/* Installers */}
                        <DetailsPageRelatedTileGrid
                            type="professionals"
                            title={`Installers of ${product.Title}`}
                            tiles={installersAdmin}
                            cta={
                                product.Installers.pageInfo.totalCount >
                                (mobile ? 2 : 4)
                                    ? {
                                          type: 'showAll',
                                      }
                                    : undefined
                            }
                            displayCount={{
                                desktop: 4,
                                mobile: 2,
                            }}
                            appDisplayMode={appDisplayMode}
                        />
                    </div>
                    {!mobile && <AboutSeller data={product} />}
                </div>
                <DetailPageButtons
                    id={product.ID}
                    editLink={productEditPath}
                    shareableLink={product.Link}
                    // The slider's photographs, as the stored originals.
                    downloadImages={{
                        itemLabel: 'Product',
                        title: product.Title,
                        getImages: () =>
                            uniqueOriginals(
                                (product.LibraryImages ?? []).map(
                                    (image) =>
                                        image?.LibraryImageFile
                                            ?.DetailPageNatural?.Desktop,
                                ),
                            ),
                    }}
                    canEdit={canEditProducts}
                    canCopyLink={canCopyLink}
                    visibilityToggle={
                        isAdminUser && setInLibrary
                            ? {
                                  isInDirectory: optimisticInLibrary,
                                  disabled:
                                      !product.Professional?.InLibrary ||
                                      !!product.Professional?.Disable,
                                  itemType: 'product',
                                  onToggle: (inLibrary) => {
                                      setOptimisticInLibrary(inLibrary);
                                      setInLibrary(
                                          'product',
                                          product.ID,
                                          inLibrary,
                                      );
                                  },
                              }
                            : undefined
                    }
                />

                {whereToBuyEnabled && (
                    <ProductLocationsDrawer
                        product={product}
                        open={whereToBuyOpen}
                        toggleModal={setWhereToBuyOpen}
                    />
                )}
            </VariantSelectionProvider>
        </div>
    );
};

/**
 * Switches between the legacy product page and the BC-4505 revamp behind the
 * `revamp_2026` CMS feature flag. The loader is shared and
 * unchanged. In local dev only, `?revamp=1` force-renders the revamp so the
 * page can be QA'd before the flag is activated on the team server.
 */
/**
 * The revamp page owns the whole layout, so it skips ProductPageLegacy and with
 * it the BM admin chrome. Re-wire the edit-mode toggle + DetailPageButtons here
 * so admins keep their controls (gated on canEditProducts as before).
 */
const ProductRevampWithAdmin = () => {
    const { product } = useLoaderData<typeof loader>();
    const { canEditProducts } = useEditPermissions(product.Professional.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmMarketingView,
        product.Professional.ID,
    );
    const productEditPath = getBMPagePath(
        product.Professional.URLSegment,
        'products',
        product.URLSegment,
    );
    useSetEditMode(productEditPath, canEditProducts);

    // B5: mirror the legacy page's visibility/in-directory toggle so admin
    // users see the same floating controls on the revamp as on the legacy PDP.
    const { user } = useAppData();
    const isAdminUser =
        user.__typename === 'Me' &&
        (user.IsStaffAdmin === true ||
            ('IsHighAdmin' in user && user.IsHighAdmin === true));
    const [optimisticInLibrary, setOptimisticInLibrary] = useState(
        !!product.InLibrary,
    );
    useEffect(() => {
        setOptimisticInLibrary(!!product.InLibrary);
    }, [product.InLibrary]);
    const { setInLibrary } = useSetInLibrary(
        (error) => {
            setOptimisticInLibrary(!!product.InLibrary);
            toast.error(error.Message || 'Something went wrong');
        },
        (_, __, inLibrary) =>
            toast.success(
                inLibrary ? 'Added to directory.' : 'Removed from directory.',
            ),
    );

    return (
        <>
            <DetailPageButtons
                id={product.ID}
                editLink={productEditPath}
                shareableLink={product.Link}
                // The slider's photographs, as the stored originals.
                downloadImages={{
                    itemLabel: 'Product',
                    title: product.Title,
                    getImages: () =>
                        uniqueOriginals(
                            (product.LibraryImages ?? []).map(
                                (image) =>
                                    image?.LibraryImageFile?.DetailPageNatural
                                        ?.Desktop,
                            ),
                        ),
                }}
                canEdit={canEditProducts}
                canCopyLink={canCopyLink}
                visibilityToggle={
                    isAdminUser && setInLibrary
                        ? {
                              isInDirectory: optimisticInLibrary,
                              disabled:
                                  !product.Professional?.InLibrary ||
                                  !!product.Professional?.Disable,
                              itemType: 'product',
                              onToggle: (inLibrary) => {
                                  setOptimisticInLibrary(inLibrary);
                                  setInLibrary(
                                      'product',
                                      product.ID,
                                      inLibrary,
                                  );
                              },
                          }
                        : undefined
                }
            />
            <ProductPageRevamp />
        </>
    );
};

const ProductPage = () => {
    const { detailsRevamp, product } = useLoaderData<typeof loader>();
    const [searchParams] = useSearchParams();

    // Published once for the route so any gate opened from this page shows the
    // product. A CTA for something narrower — a chosen variant, a tile in a
    // grid — still passes its own image, which wins over this.
    const productImage = product.LibraryImages?.[0]?.LibraryImageFile;
    useGateSubject({
        kind: 'product',
        id: product.ID,
        title: product.Title,
        // PhotoTile is sized for a grid tile and goes soft across the 478px
        // pane, so the detail-page rendition leads.
        hero: productImage?.DetailPageSquare?.Desktop ?? undefined,
        tile: productImage?.PhotoTile ?? undefined,
    });
    // `revamp_2026` is the umbrella flag for the whole details revamp
    // (page + Magic Island). Dev overrides: `?revamp=1` or `?detailsRevamp=1`.
    const showRevamp =
        detailsRevamp ||
        (import.meta.env.DEV &&
            (searchParams.get('revamp') === '1' ||
                searchParams.get('detailsRevamp') === '1'));

    return showRevamp ? <ProductRevampWithAdmin /> : <ProductPageLegacy />;
};

export default ProductPage;
